import React, { useState, useEffect } from 'react';
import NotesList from './components/NotesList';
import NoteForm from './components/NoteForm';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function App() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingNote, setEditingNote] = useState(null);

  // Fetch all notes
  const fetchNotes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/notes`);

      if (!response.ok) {
        throw new Error(`Failed to fetch notes: ${response.statusText}`);
      }

      const data = await response.json();
      setNotes(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching notes:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load notes on component mount
  useEffect(() => {
    fetchNotes();
  }, []);

  // Create a new note
  const handleCreateNote = async (noteData) => {
    try {
      setError(null);
      const response = await fetch(`${API_URL}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(noteData),
      });

      if (!response.ok) {
        throw new Error(`Failed to create note: ${response.statusText}`);
      }

      const newNote = await response.json();
      setNotes([newNote, ...notes]);
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Error creating note:', err);
      return false;
    }
  };

  // Update an existing note
  const handleUpdateNote = async (id, noteData) => {
    try {
      setError(null);
      const response = await fetch(`${API_URL}/notes/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(noteData),
      });

      if (!response.ok) {
        throw new Error(`Failed to update note: ${response.statusText}`);
      }

      const updatedNote = await response.json();
      setNotes(notes.map(note => note.id === id ? updatedNote : note));
      setEditingNote(null);
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Error updating note:', err);
      return false;
    }
  };

  // Delete a note
  const handleDeleteNote = async (id) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      setError(null);
      const response = await fetch(`${API_URL}/notes/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete note: ${response.statusText}`);
      }

      setNotes(notes.filter(note => note.id !== id));
    } catch (err) {
      setError(err.message);
      console.error('Error deleting note:', err);
    }
  };

  // Handle edit button click
  const handleEditNote = (note) => {
    setEditingNote(note);
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingNote(null);
  };

  return (
    <div className="app">
      <header className="app-header">
        <h1>📝 Notes App</h1>
        <p>Keep track of your thoughts and ideas</p>
      </header>

      <main className="app-main">
        <div className="container">
          {error && (
            <div className="error-message" role="alert">
              <strong>Error:</strong> {error}
            </div>
          )}

          <section className="note-form-section">
            <h2>{editingNote ? 'Edit Note' : 'Create New Note'}</h2>
            <NoteForm
              onSubmit={editingNote ? handleUpdateNote : handleCreateNote}
              editingNote={editingNote}
              onCancel={editingNote ? handleCancelEdit : null}
            />
          </section>

          <section className="notes-list-section">
            <h2>Your Notes</h2>
            {loading ? (
              <div className="loading" role="status">
                <p>Loading notes...</p>
              </div>
            ) : (
              <NotesList
                notes={notes}
                onEdit={handleEditNote}
                onDelete={handleDeleteNote}
              />
            )}
          </section>
        </div>
      </main>

      <footer className="app-footer">
        <p>&copy; 2024 Notes App. Built with React.</p>
      </footer>
    </div>
  );
}

export default App;
